import { Flex, Group } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { TableColumn } from '../../../components/table/table';
import { toLocalDate } from '../../../helpers/date-time-helpers';
import { resolveTaskTypeLabel } from '../../../helpers/task-type-labels/task-type-labels';
import { secondaryBase } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { TruncatedText } from '../../adherence/risk-analysis/truncated-text';
import { EpicCell, InitiativeCell } from '../../tasks-table/tasks-table-cells';
import { getStoryPointsText, getTaskTypeText } from '../../tasks-table/tasks-table.helpers';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { TaskSortBy } from './task-detail.type';

/**
 * Sorts an array of task records based on the specified sort criteria.
 *
 * @param {TaskRecord[]} tasks - Array of TaskRecord objects to be sorted
 * @param {TaskSortBy | null} sortBy - The sorting criteria to apply (CREATED_DATE, POINTS, TYPE, or null)
 * @returns {TaskRecord[]} A new sorted array of TaskRecord objects
 */

function sortTasks(tasks: TaskRecord[], sortBy: TaskSortBy | null): TaskRecord[] {
  if (!sortBy) return tasks;

  switch (sortBy) {
    case TaskSortBy.CREATED_DATE:
      return [...tasks].sort((a, b) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime());
    case TaskSortBy.POINTS:
      return [...tasks].sort((a, b) => (a.points ?? 0) - (b.points ?? 0));
    case TaskSortBy.TYPE:
      return [...tasks].sort((a, b) => (a.type ?? '').localeCompare(b.type ?? ''));
    default:
      return tasks;
  }
}

/**
 * Returns an array of column configurations for the task table.
 *
 * This function defines the structure and rendering behavior for each column
 * in the task table, including column keys, labels, sorting capabilities,
 * custom rendering functions, and width specifications.
 *
 * @returns {TableColumn<TaskRecord>[]} An array of column configuration objects
 * that specify how to display and interact with task data in a table format
 */

function getTaskTableColumns(t: TFunction): TableColumn<TaskRecord>[] {
  const TEXT_SIZE = 'small';
  return [
    {
      key: 'title',
      label: t('work_periods.task_detail.key'),
      sortable: true,
      render: (_, task) => (
        <a
          href={task.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: secondaryBase }}
        >
          <Group gap={2}>
            <Text size={TEXT_SIZE}>{task.title}</Text>
            <Icon name="open_in_new" size={14} />
          </Group>
        </a>
      ),
      width: '10%',
    },
    {
      key: 'name',
      label: t('work_periods.task_detail.name'),
      sortable: true,
      render: (_, task) => (
        <TruncatedText text={task.name?.toString() || '-'} onToggle={() => {}} textSize={TEXT_SIZE} />
      ),
      width: '15%',
    },
    {
      key: 'epic',
      label: t('work_periods.task_detail.epic_key'),
      sortable: true,
      render: (_, task) =>
        task.epic ? (
          <a
            href={task.epic.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: secondaryBase }}
          >
            <Group gap={2}>
              <Text size={TEXT_SIZE}>{task.epic?.title?.toString() || '-'}</Text>
              <Icon name="open_in_new" size={14} />
            </Group>
          </a>
        ) : (
          <EpicCell epic={task.epic} />
        ),
      width: '8%',
    },
    {
      key: 'epic_name' as keyof TaskRecord,
      label: `${t('common.parent_task')}`,
      sortable: true,
      render: (_, task) => <EpicCell epic={task.epic} />,
      width: '10%',
    },
    {
      key: 'initiatives',
      label: t('common.initiatives'),
      sortable: true,
      render: (_, task) => <InitiativeCell initiatives={task.initiatives} />,
      width: '10%',
    },
    {
      key: 'points',
      label: t('common.estimation'),
      sortable: true,
      render: (_, task) => <Text size={TEXT_SIZE}>{getStoryPointsText(task.points, t)}</Text>,
      width: '8%',
    },
    {
      key: 'type',
      label: t('common.type'),
      sortable: true,
      render: (_, task) => <Text size={TEXT_SIZE}>{getTaskTypeText(task.type, resolveTaskTypeLabel(task), t)}</Text>,
      width: '8%',
    },
    {
      key: 'created_date',
      label: t('common.created'),
      sortable: true,
      render: (value) => <Text size={TEXT_SIZE}>{new Date(value?.toString() || '-').toLocaleDateString() || '-'}</Text>,
      width: '8%',
    },
    {
      key: 'transitions',
      label: t('common.status_history'),
      sortable: true,
      render: (_, task) => (
        <Flex direction="column" gap={4}>
          {task.transitions?.map((transition, index) => {
            return (
              <div key={index} style={{}}>
                {`${transition.status} - ${transition.changed_by ? transition.changed_by : '-'} (${toLocalDate(
                  transition.changed_at,
                ).format('MM/DD/YYYY')})`}
              </div>
            );
          })}
        </Flex>
      ),
      width: '35%',
    },
  ];
}

function sortTaskData(data: TaskRecord[], sortBy: keyof TaskRecord, direction: 'asc' | 'desc'): TaskRecord[] {
  return [...data].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === null || aValue === undefined) return direction === 'asc' ? 1 : -1;
    if (bValue === null || bValue === undefined) return direction === 'asc' ? -1 : 1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });
}

export { getTaskTableColumns, sortTaskData, sortTasks };
